import axios, { AxiosError, AxiosInstance } from 'axios';
import Cookie from 'js-cookie';
import { captureException, captureMessage } from '@sentry/react';

import { ErrorHandlerType, StorageHeaderValueType } from './types';
import { userStore } from 'stores/userStore';

export const EFKT_SITE_URL = 'https://efkt.com';

export const IS_ESOFT_USER_KEY = 'esoft.user';
export const HEADER_USER_ID_KEY = 'header.user.id';
export const AUTH_LOGIN_KEY = 'auth.login';
export const AUTH_LOGOUT_KEY = 'auth.logout';
export const AUTH_SCHEMA_KEY = 'auth.schema';
export const AUTH_REFRESHING = 'auth.refreshing';
export const RESET_PASSWORD_CODE_KEY = 'password.error.code';
export const AUTH_RESET_PASSWORD_KEY = 'auth.reset.password';
export const AUTH_REDIRECT_URL = 'auth.redirect.url';
export const MINDWORKING_REDIRECT_REFERENCE = 'mindworking.reference';
export const LAST_DEPARTMENT_ID = 'branchId';
const TOKEN_KEY = 'auth.token';
const TOKEN_PERSISTENCE = 365; // days
let errorHandler: ErrorHandlerType | undefined;
const blacklistedAuthorizationUrls = ['amazonaws.com'];

export const endpoints = {
  datahub: import.meta.env.VITE_DATAHUB_API_ENDPOINT || 'https://datahub.stage.efkt.net',
};

function forgetAuthToken(): void {
  if (typeof window !== 'undefined') {
    Cookie.remove(TOKEN_KEY);
    const logoutUrl = localStorage.getItem(AUTH_LOGOUT_KEY);
    if (logoutUrl) {
      window.location.href = logoutUrl;
    }
  }
}

function getAuthToken(): string | undefined {
  return typeof window !== 'undefined' ? Cookie.get(TOKEN_KEY) : undefined;
}

function setAuthToken(value: string, persist: boolean): void {
  Cookie.set(TOKEN_KEY, value, {
    expires: persist ? TOKEN_PERSISTENCE : undefined,
  });
}

function createClient(baseURL: string, appendAuthorizationHeader = true): AxiosInstance {
  const client = axios.create({
    baseURL,
  });

  if (appendAuthorizationHeader) {
    client.interceptors.request.use(requestConfig => {
      const token = getAuthToken();
      if (token && !blacklistedAuthorizationUrls.some(url => requestConfig.url?.indexOf(url) !== -1)) {
        const authSchema = localStorage.getItem(AUTH_SCHEMA_KEY);
        requestConfig.headers.Authorization = `${authSchema ? `${authSchema} ` : ''}${token}`;
      }

      try {
        const userIdHeader: StorageHeaderValueType = JSON.parse(localStorage.getItem(HEADER_USER_ID_KEY) || '');
        if (userIdHeader?.value && !requestConfig.url?.includes('user/branches')) {
          requestConfig.headers[userIdHeader.header] = userIdHeader.value;
        }
      } catch (ex) {
        // noop
      }

      return requestConfig;
    });
  }

  client.interceptors.response.use(undefined, (error: AxiosError) => {
    if (errorHandler) {
      errorHandler(error);
    }

    const userInfo = userStore.getState().user;

    captureException(error);
    captureMessage(error.message, {
      user: { email: userInfo?.email, id: userInfo?.id, fullName: userInfo?.fullName },
      level: 'error',
    });
    if (error?.response?.status === 401) {
      forgetAuthToken();
    }
    return Promise.reject(error);
  });

  return client;
}

function setErrorHandler(handler: ErrorHandlerType) {
  errorHandler = handler;
}

export default {
  createClient,
  getAuthToken,
  setAuthToken,
  forgetAuthToken,
  setErrorHandler,
};
